import { AlertCircle } from "lucide-react";
import { useEffect, useState, type FC } from "react";

import { Form, StickyFormFooter } from "@/components/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { FreeTrialEndedModal } from "../../components/common/FreeTrialEndedModal";
import { useFreeTrialStatus } from "../../hooks/useFreeTrialStatus";
import { DataManagementSection } from "./components/DataManagementSection";
import { DeleteCaseDialog } from "./components/DeleteCaseDialog";
import { EmployerInfoSection } from "./components/EmployerInfoSection";
import { PersonalInfoSection } from "./components/PersonalInfoSection";
import { SettingsNav, type SettingsSectionId } from "./components/SettingsNav";
import { useCaseSettingsForm } from "./useCaseSettingsForm";

/**
 * Case Settings: section navigation (personal, employer, data management)
 * beside the active section's form. Rendered as an IDE tab and on the legacy
 * `/edit` routes; reads the individual id from the route.
 */
export const CaseSettings: FC = () => {
  const form = useCaseSettingsForm();
  const [section, setSection] = useState<SettingsSectionId>("personal");
  const { daysLeft } = useFreeTrialStatus();
  const [showTrialEndedModal, setShowTrialEndedModal] = useState(false);

  useEffect(() => {
    if (daysLeft !== null && daysLeft <= 0) setShowTrialEndedModal(true);
  }, [daysLeft]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-background">
      {form.errorMessage ? (
        <Alert variant="destructive" className="m-3 mb-0">
          <AlertCircle />
          <AlertTitle>Oops! Something went wrong.</AlertTitle>
          <AlertDescription>{form.errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex min-h-0 w-full flex-1 flex-col items-stretch overflow-hidden md:flex-row">
        <SettingsNav active={section} onSelect={setSection} />
        <div
          role="tabpanel"
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        >
          {section === "data" ? (
            <DataManagementSection form={form} />
          ) : (
            <Form
              form={form.form}
              onSubmit={form.submit}
              className="flex h-full min-h-0 flex-col gap-0"
            >
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {section === "personal" ? (
                  <PersonalInfoSection form={form} />
                ) : (
                  <EmployerInfoSection form={form} />
                )}
              </div>
              <StickyFormFooter
                isDirty={form.isDirty}
                isSubmitting={form.isSaving}
                canSubmit={form.isReady}
                onCancel={form.handleDiscardChanges}
                cancelLabel="Discard changes"
              />
            </Form>
          )}
        </div>
      </div>

      <DeleteCaseDialog form={form} />
      <FreeTrialEndedModal
        inCase
        isOpen={showTrialEndedModal}
        onClose={() => setShowTrialEndedModal(false)}
      />
    </div>
  );
};
